import React, { useEffect, useMemo, useState } from "react";

import SportsDeltaTable from "./sportsDeltaTable";
import { Box, CircularProgress, Typography, Button, Stack, Chip } from "@mui/material";

export default function SportsDashboard(props) {
  const {
    getSportsData,
    getSportsDeltaSuccess,
    getSportsDeltaError,
    getSportsDeltaLoader
  } = props;

  const [flash, setFlash] = useState(false);
  const payload = useMemo(() => {
    return (
      (getSportsDeltaSuccess &&
        (getSportsDeltaSuccess.data || getSportsDeltaSuccess.payload)) ||
      getSportsDeltaSuccess ||
      null
    );
  }, [getSportsDeltaSuccess]);

  // Kick off stream
  useEffect(() => {
    if (getSportsData) getSportsData({});
  }, []); // eslint-disable-line

  // Light up a tiny indicator whenever a new event lands
  useEffect(() => {
    if (!payload) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 700);
    return () => clearTimeout(t);
  }, [payload && (payload.server_ts || payload._event_id)]); // server time from backend

  const handleRefresh = () => getSportsData && getSportsData({});

  // Spinner / error
  if (getSportsDeltaLoader) {
    return (
      <Box p={3} display="flex" alignItems="center" justifyContent="center" height="65vh">
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">Loading sports delta…</Typography>
        </Stack>
      </Box>
    );
  }

  if (getSportsDeltaError) {
    return (
      <Box p={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Sports Delta</Typography>
          <Button variant="outlined" onClick={handleRefresh}>Retry</Button>
        </Stack>
        <Typography variant="body2" color="error">
          {typeof getSportsDeltaError === "string"
            ? getSportsDeltaError
            : (getSportsDeltaError && getSportsDeltaError.message) || "Failed to load data."}
        </Typography>
      </Box>
    );
  }

  const lastUpdated = payload && payload.server_ts; // comes from backend

  return (
    <Box p={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Sports Delta</Typography>

          {/* Live dot that flashes on update */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: flash ? "success.main" : "grey.400",
              transition: "background-color 300ms ease"
            }}
            title={flash ? "New data received" : "Live"}
          />

          {/* “Last updated” chip from server time */}
          {lastUpdated && (
            <Chip
              size="small"
              variant="outlined"
              label={`Updated ${new Date(lastUpdated).toLocaleTimeString()}`}
            />
          )}
        </Stack>

        <Button size="small" variant="outlined" onClick={handleRefresh}>Refresh</Button>
      </Stack>

      {payload ? (
        <SportsDeltaTable data={payload} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No data yet. Try Refresh.
        </Typography>
      )}
    </Box>
  );
}
